import React, { useEffect, useState, useCallback } from 'react';
import api from '../../config/axios';
import { orderSubtotal, orderTax, orderTotal, taxRateLabel } from '../../utils/tax';
import Header from '../common/Header';
import { useNavigate, useLocation } from 'react-router-dom';
import PaymentSuccessModal from './PaymentSuccessModal';
import './OrderPayment.css';

interface Order {
  _id: string;
  items: Array<{ name: string; price: number; quantity: number; imageUrl?: string; taxRate?: number | null; tax?: number | null }>;
  total: number;
  isPaid?: boolean;
  amountPaid?: number;
  change?: number;
  createdAt: string;
  subtotal?: number;
  taxRate?: number;
  tax?: number;
  discount?: number;
  status?: 'pending' | 'paid' | 'cancelled' | 'processing' | 'merged';
  customerName?: string;
  tableId?: string | { _id: string; name: string };
  mergedTables?: Array<{ _id: string; name: string }>;
}

interface OrderPaymentProps {
  user: any;
}

const OrderPayment: React.FC<OrderPaymentProps> = ({ user }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [amountPaid, setAmountPaid] = useState<number>(0);
  const [change, setChange] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Xem ghi chú ở OrderCreate: input giữ chuỗi thô, số chỉ suy ra khi tính.
  const [discountInput, setDiscountInput] = useState<string>('');
  const editDiscount = Math.max(0, Number(discountInput) || 0);
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);
  const [isMerging, setIsMerging] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'momo'>('cash');
  const [momoLoading, setMomoLoading] = useState(false);
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [paidOrderData, setPaidOrderData] = useState<any>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Thuế đã chốt trên từng món khi đặt; nhân viên không sửa.
  const getCalculatedTotal = useCallback(() => {
    if (!selectedOrder) return 0;
    return orderSubtotal(selectedOrder) + orderTax(selectedOrder) - editDiscount;
  }, [selectedOrder, editDiscount]);

  // Tổng phải thu khi gộp nhiều đơn = tổng tiền của từng đơn (đã gồm thuế theo món).
  const selectedOrdersTotal = selectedOrders.reduce((sum, o) => sum + orderTotal(o), 0);

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/orders');
      const pendingOrders = res.data.data.filter((o: Order) => o.status === 'pending' || o.status === 'processing');
      setOrders(pendingOrders);

      // Chọn sẵn đơn hàng khi được điều hướng kèm orderId (từ trang tạo đơn
      // hoặc từ chi tiết đơn trong lịch sử đơn hàng).
      if (location.state && location.state.orderId) {
        const targetOrder = pendingOrders.find((o: Order) => o._id === location.state.orderId);
        if (targetOrder) {
          setSelectedOrder(targetOrder);
          setDiscountInput(String(targetOrder.discount || 0));
          if (location.state.fromCreate) {
            setSuccess('Đơn hàng đã được tạo thành công! Vui lòng thanh toán.');
          }
        }
      }
    } catch (err) {
      setError('Không thể tải đơn hàng');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [location.state]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchOrders();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  useEffect(() => {
    const total = getCalculatedTotal();
    setChange(amountPaid - total);
  }, [amountPaid, editDiscount, selectedOrder, getCalculatedTotal]);

  const handleSelectOrder = (order: Order) => {
    setSelectedOrder(order);
    setDiscountInput(String(order.discount || 0));
    setAmountPaid(0);
    setChange(0);
    setError('');
    setSuccess('');
  };

  const handleAmountPaidChange = (value: string) => {
    const num = Number(value.replace(/\D/g, '')); // bỏ dấu phân cách hàng nghìn
    setAmountPaid(num);
    if (selectedOrder) {
      setChange(num - getCalculatedTotal());
    }
  };

  // Gợi ý số tiền: đủ tiền + các mệnh giá lớn hơn tổng cộng
  const getQuickAmounts = () => {
    const total = getCalculatedTotal();
    const notes = [10000, 20000, 50000, 100000, 200000, 500000].filter(n => n > total);
    return [total, ...notes];
  };

  const handlePay = async () => {
    if (!selectedOrder) return;
    setError('');
    setSuccess('');
    const total = getCalculatedTotal();

    if (paymentMethod === 'cash') {
      if (amountPaid < total) {
        setError('Số tiền khách đưa chưa đủ để thanh toán!');
        return;
      }
      try {
        setLoading(true);
        const res = await api.post(
          `/api/orders/${selectedOrder._id}/pay`,
          { amountPaid, discount: editDiscount }
        );
        setPaidOrderData({
          ...res.data.data,
          tableId: selectedOrder.tableId
        });
        setShowPaymentSuccessModal(true);

        setOrders(orders.filter(o => o._id !== selectedOrder._id));
        setSelectedOrder(null);
        setAmountPaid(0);
        setChange(0);

      } catch (err: any) {
        setError(err.response?.data?.message || 'Lỗi khi thanh toán');
      } finally {
        setLoading(false);
      }
    } else if (paymentMethod === 'momo') {
      try {
        setMomoLoading(true);
        const res = await api.post(
          '/api/payment/momo/create',
          {
            orderId: selectedOrder._id,
            amount: total,
            orderInfo: `Thanh toan don hang ${selectedOrder._id.slice(-6).toUpperCase()}`
          }
        );

        if (res.data.success) {
          if (res.data.data.payUrl) {
            window.location.href = res.data.data.payUrl;
          } else {
            setSuccess(res.data.data.message || 'Đơn hàng đã được xử lý thanh toán.');
          }

          const checkPaymentStatus = async () => {
            try {
              const statusRes = await api.get(
                `/api/payment/momo/status/${selectedOrder._id}`
              );

              if (statusRes.data.data.status === 'paid') {
                setSuccess('Thanh toán Momo thành công!');
                setOrders(orders.filter(o => o._id !== selectedOrder._id));
                setSelectedOrder(null);
                setAmountPaid(0);
                setChange(0);
                return;
              }

              setTimeout(checkPaymentStatus, 3000);
            } catch (err) {
              console.error('Error checking payment status:', err);
            }
          };

          setTimeout(checkPaymentStatus, 5000);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Lỗi khi tạo thanh toán Momo');
      } finally {
        setMomoLoading(false);
      }
    }
  };

  const getChangeClass = () => {
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'zero';
  };

  const onLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const handleClosePaymentSuccessModal = () => {
    setShowPaymentSuccessModal(false);
    if (location.state && location.state.fromCreate) {
      navigate('/');
    }
  };

  return (
    <>
      <Header user={user} onLogout={onLogout}>
        <span className="user-role">{user.role === 'admin' ? 'Quản lý' : 'Nhân viên'}</span>
      </Header>
      <div className="order-payment-bg">
        <div className="order-payment-container">
          <div className="order-payment-header">
            <button
              onClick={() => navigate('/')}
              className="order-payment-back-btn"
              title="Quay lại Dashboard"
            >
              ←
            </button>
            <h2 className="order-payment-title">THANH TOÁN ĐƠN HÀNG</h2>
          </div>

          {error && <div className="order-payment-message order-payment-error">{error}</div>}
          {success && <div className="order-payment-message order-payment-success">{success}</div>}
          {loading && <div className="order-payment-loading">Đang tải...</div>}

          <div className="order-payment-main">
            <div className="order-payment-left">
              <h3 className="order-payment-section-title">Đơn chưa thanh toán</h3>
              {orders.length === 0 ? (
                <div className="order-payment-empty">Không có đơn nào chờ thanh toán.</div>
              ) : (
                <>
                  <table className="order-payment-table">
                    <thead>
                      <tr>
                        <th>Mã đơn</th>
                        <th>Tên khách</th>
                        <th>Bàn</th>
                        <th>Thời gian</th>
                        <th>Tổng tiền</th>
                        <th>Trạng thái</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map(order => (
                        <tr
                          key={order._id}
                          className={`order-payment-row${selectedOrder?._id === order._id ? ' selected' : ''}`}
                          onClick={() => handleSelectOrder(order)}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedOrders.some(o => o._id === order._id)}
                              onClick={e => e.stopPropagation()}
                              onChange={e => {
                                if (e.target.checked) {
                                  setSelectedOrders(prev => [...prev, order]);
                                } else {
                                  setSelectedOrders(prev => prev.filter(o => o._id !== order._id));
                                }
                              }}
                              style={{ marginRight: 6 }}
                            />
                            <span className="order-payment-id">{order._id.slice(-6).toUpperCase()}</span>
                          </td>
                          <td>{order.customerName || '-'}</td>
                          <td>
                            {order.mergedTables && order.mergedTables.length > 0
                              ? order.mergedTables.map((table: any) => table.name).join(', ')
                              : typeof order.tableId === 'object' && order.tableId !== null
                                ? order.tableId.name
                                : order.tableId || '-'
                            }
                          </td>
                          <td>{new Date(order.createdAt).toLocaleString()}</td>
                          <td className="order-payment-total">{order.total.toLocaleString()} đ</td>
                          <td>{order.status === 'pending' ? 'Chờ thanh toán' : order.status === 'processing' ? 'Đang xử lý' : order.status === 'paid' ? 'Đã thanh toán' : order.status === 'cancelled' ? 'Đã hủy' : order.status}</td>
                          <td>
                            <button
                              onClick={() => handleSelectOrder(order)}
                              className="order-payment-select-btn"
                            >
                              Chọn
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {selectedOrders.length >= 2 && (
                    <div className="order-payment-merge-actions">
                      <button
                        className="order-payment-merge-btn"
                        onClick={() => {
                          setSelectedOrder(null);
                          setError('');
                          setSuccess('');
                          setIsMerging(true);
                        }}
                      >
                        Gom bàn ({selectedOrders.length})
                      </button>
                      <button
                        className="order-payment-cancel-btn"
                        onClick={() => {
                          setSelectedOrders([]);
                          setIsMerging(false);
                        }}
                      >
                        Hủy
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="order-payment-right">
              <h3 className="order-payment-section-title">Thanh toán</h3>
              {isMerging && selectedOrders.length >= 2 ? (
                <div className="order-payment-form" style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24, marginTop: 8, minWidth: 340 }}>
                  <div className="order-payment-info">
                    <div className="order-payment-info-item" style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                      <span className="order-payment-info-label">Số bàn đã chọn:</span>
                      <span className="order-payment-info-value">{selectedOrders.length}</span>
                    </div>
                    {selectedOrders.map((order, idx) => {
                      const subtotal = orderSubtotal(order);
                      const tax = orderTax(order);
                      const discount = order.discount || 0;
                      const total = orderTotal(order);
                      return (
                        <div key={order._id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, margin: '14px 0', padding: 12, background: '#f6faff', boxShadow: '0 1px 6px rgba(49,130,206,0.06)' }}>
                          <div style={{ fontWeight: 600, marginBottom: 4, color: '#3182ce', fontSize: 15 }}>
                            Bàn: {
                              order.mergedTables && order.mergedTables.length > 0
                                ? order.mergedTables.map((table: any) => table.name).join(', ')
                                : typeof order.tableId === 'object' && order.tableId !== null
                                  ? order.tableId.name
                                  : order.tableId || '-'
                            }
                            <span style={{ marginLeft: 12, color: '#888', fontWeight: 400, fontSize: 13 }}>Mã đơn: {order._id.slice(-6).toUpperCase()}</span>
                          </div>
                          <div style={{ marginBottom: 4 }}>
                            <b style={{ color: '#222' }}>Menu:</b>
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
                              {order.items.map((item, i) => (
                                <li key={i}>
                                  {item.name} x {item.quantity} <span style={{ color: '#888' }}>- {(item.price * item.quantity).toLocaleString()} đ</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 14, marginTop: 4 }}>
                            <span>Tạm tính: <b>{subtotal.toLocaleString()} đ</b></span>
                            <span>Thuế: <b>{Math.round(tax).toLocaleString()} đ</b> ({taxRateLabel(order)})</span>
                            <span>Giảm giá: <b>{discount.toLocaleString()} đ</b></span>
                            <span>Tổng: <b style={{ color: '#3182ce' }}>{Math.round(total).toLocaleString()} đ</b></span>
                          </div>
                        </div>
                      );
                    })}
                    <div className="order-payment-info-item" style={{ fontWeight: 700, fontSize: 20, borderTop: '1px solid #e2e8f0', paddingTop: 10, marginTop: 10, color: '#222' }}>
                      <span className="order-payment-info-label">Tổng cộng:</span>
                      <span className="order-payment-info-value order-payment-total" style={{ color: '#3182ce' }}>
                        {Math.round(selectedOrdersTotal).toLocaleString()} đ
                      </span>
                    </div>
                  </div>
                  <div className="order-payment-amount-group">
                    <label className="order-payment-amount-label">
                      <b>Tiền khách đưa:</b>
                    </label>
                    <input
                      type="number"
                      min={selectedOrdersTotal}
                      value={amountPaid || ''}
                      onChange={e => setAmountPaid(Number(e.target.value))}
                      className="order-payment-amount-input"
                      placeholder="Nhập số tiền..."
                    />
                  </div>
                  <div className="order-payment-change">
                    <span className="order-payment-change-label">Tiền thối lại:</span>
                    <span className={`order-payment-change-value ${change > 0 ? 'positive' : change < 0 ? 'negative' : 'zero'}`}>
                      {(amountPaid - selectedOrdersTotal).toLocaleString()} đ
                    </span>
                  </div>
                  <button
                    className="order-payment-pay-btn"
                    disabled={loading || amountPaid < selectedOrdersTotal}
                    onClick={async () => {
                      setError('');
                      setSuccess('');
                      if (amountPaid < selectedOrdersTotal) {
                        setError('Số tiền khách đưa chưa đủ để thanh toán!');
                        return;
                      }
                      try {
                        setLoading(true);

                        const totalAmount = selectedOrdersTotal;

                        const response = await api.post('/api/orders/pay-multiple', {
                          orderIds: selectedOrders.map(o => o._id),
                          amountPaid: amountPaid
                        });

                        const paidOrderData = {
                          ...response.data.data,
                          isMerged: true,
                          mergedOrders: selectedOrders.map(order => ({
                            _id: order._id,
                            tableId: order.tableId
                          })),
                          orderCount: selectedOrders.length,
                          mergedTables: response.data.data.mergedTables || null
                        };

                        setPaidOrderData(paidOrderData);
                        setShowPaymentSuccessModal(true);

                        setOrders(orders.filter(o => !selectedOrders.some(sel => sel._id === o._id)));
                        setSelectedOrders([]);
                        setAmountPaid(0);
                        setChange(0);
                        setIsMerging(false);

                      } catch (err: any) {
                        setError(err.response?.data?.message || 'Lỗi khi thanh toán gom bàn');
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    {loading ? 'Đang xử lý...' : 'Xác nhận thanh toán gom bàn'}
                  </button>
                </div>
              ) : (
                !selectedOrder ? (
                  <div className="order-payment-empty">Chọn đơn để thanh toán.</div>
                ) : (
                  <div className="order-payment-form">
                    <div className="order-payment-info">
                      <div className="order-payment-info-item">
                        <span className="order-payment-info-label">Mã đơn:</span>
                        <span className="order-payment-info-value">
                          <span className="order-payment-id">{selectedOrder._id.slice(-6).toUpperCase()}</span>
                        </span>
                      </div>
                      <div className="order-payment-info-item">
                        <span className="order-payment-info-label">Tên khách:</span>
                        <span className="order-payment-info-value">{selectedOrder.customerName || '-'}</span>
                      </div>
                      <div className="order-payment-info-item">
                        <span className="order-payment-info-label">Bàn:</span>
                        <span className="order-payment-info-value">
                          {selectedOrder.mergedTables && selectedOrder.mergedTables.length > 0
                            ? selectedOrder.mergedTables.map((table: any) => table.name).join(', ')
                            : typeof selectedOrder.tableId === 'object' && selectedOrder.tableId !== null
                              ? selectedOrder.tableId.name
                              : selectedOrder.tableId || '-'
                          }
                        </span>
                      </div>
                      <div className="order-payment-info-item">
                        <span className="order-payment-info-label">Thời gian:</span>
                        <span className="order-payment-info-value">{new Date(selectedOrder.createdAt).toLocaleString()}</span>
                      </div>
                      <div className="order-payment-info-item menu-list" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                        <span className="order-payment-info-label" style={{ marginBottom: 2 }}>Menu:</span>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
                          {selectedOrder.items.map((item, i) => (
                            <li key={i}>
                              {item.name} x {item.quantity} <span style={{ color: '#888' }}>- {(item.price * item.quantity).toLocaleString()} đ</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {typeof selectedOrder.subtotal === 'number' ? (
                        <>
                          <div className="order-payment-info-item">
                            <span className="order-payment-info-label">Tạm tính:</span>
                            <span className="order-payment-info-value">{selectedOrder.subtotal.toLocaleString()} đ</span>
                          </div>

                          <div className="order-payment-info-item">
                            <span className="order-payment-info-label">Thuế ({taxRateLabel(selectedOrder)}):</span>
                            <span className="order-payment-info-value">
                              {Math.round(orderTax(selectedOrder)).toLocaleString()} đ
                            </span>
                          </div>
                          <div className="order-payment-info-item">
                            <span className="order-payment-info-label">Giảm giá (đ):</span>
                            <input
                              type="number"
                              min={0}
                              value={discountInput}
                              onChange={e => setDiscountInput(e.target.value)}
                              className="order-payment-amount-input"
                              style={{ width: 120, marginLeft: 8 }}
                            />
                          </div>
                          <div className="order-payment-info-item" style={{ fontWeight: 700, fontSize: 18, borderTop: '1px solid #e2e8f0', paddingTop: 6, marginTop: 4 }}>
                            <span className="order-payment-info-label">Tổng cộng:</span>
                            <span className="order-payment-info-value order-payment-total">
                              {selectedOrder && selectedOrder.items && selectedOrder.items.length > 0
                                ? getCalculatedTotal().toLocaleString() + ' đ'
                                : ''}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="order-payment-info-item" style={{ fontWeight: 700, fontSize: 18, borderTop: '1px solid #e2e8f0', paddingTop: 6, marginTop: 4 }}>
                          <span className="order-payment-info-label">Tổng cộng:</span>
                          <span className="order-payment-info-value order-payment-total">{getCalculatedTotal().toLocaleString()} đ</span>
                        </div>
                      )}
                    </div>

                    <div className="order-payment-method-group">
                      <label className="order-payment-amount-label">
                        <b>Phương thức thanh toán:</b>
                      </label>
                      <div className="order-payment-method-options">
                        <label className="order-payment-method-option">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="cash"
                            checked={paymentMethod === 'cash'}
                            onChange={e => setPaymentMethod(e.target.value as 'cash' | 'momo')}
                          />
                          <span>Tiền mặt</span>
                        </label>
                        <label className="order-payment-method-option">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="momo"
                            checked={paymentMethod === 'momo'}
                            onChange={e => setPaymentMethod(e.target.value as 'cash' | 'momo')}
                          />
                          <span>Momo</span>
                        </label>
                      </div>
                    </div>

                    {paymentMethod === 'cash' && (
                      <div className="order-payment-amount-group">
                        <label className="order-payment-amount-label">
                          <b>Tiền khách đưa:</b>
                        </label>
                        <div className="order-payment-quick-amounts">
                          {getQuickAmounts().map((amt, i) => (
                            <button
                              type="button"
                              key={amt}
                              className={`order-payment-quick-btn${amountPaid === amt ? ' active' : ''}`}
                              onClick={() => handleAmountPaidChange(String(amt))}
                            >
                              {i === 0 ? `Đủ tiền (${amt.toLocaleString()})` : amt.toLocaleString()}
                            </button>
                          ))}
                        </div>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={amountPaid ? amountPaid.toLocaleString() : ''}
                          onChange={e => handleAmountPaidChange(e.target.value)}
                          className="order-payment-amount-input"
                          placeholder="Nhập số tiền..."
                        />
                      </div>
                    )}

                    {paymentMethod === 'cash' && (
                      <div className="order-payment-change">
                        <span className="order-payment-change-label">Tiền thối lại:</span>
                        <span className={`order-payment-change-value ${getChangeClass()}`}>
                          {change.toLocaleString()} đ
                        </span>
                      </div>
                    )}

                    <button
                      onClick={handlePay}
                      className="order-payment-pay-btn"
                      disabled={loading || momoLoading || (paymentMethod === 'cash' && amountPaid < getCalculatedTotal())}
                    >
                      {loading || momoLoading ? 'Đang xử lý...' :
                       paymentMethod === 'momo' ? 'Thanh toán qua Momo' : 'Xác nhận thanh toán'}
                    </button>


                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      <PaymentSuccessModal
        isVisible={showPaymentSuccessModal}
        orderData={paidOrderData}
        onClose={handleClosePaymentSuccessModal}
      />
    </>
  );
};

export default OrderPayment;